import { Col, Form, Row, Tooltip } from 'antd';
import classNames from 'classnames';
import { useCallback, useContext, useEffect } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import IconButton from '../../components/buttons/IconButton';
import { InlineLink } from '../../components/typography';
import { FiltersAndSortContext } from '../../components/data/FiltersAndSortProvider';
import {
  DialogsParentContext,
  DialogsParentProvider,
} from '../../components/dialogs/DialogsParentContext';
import { useHasDialogStyles } from '../../components/dialogs/hasDialog.styles';
import { NoLabelForm, useFormContext } from '../../components/forms/forms';
import { ConfigurableCard } from '../../components/layout/cardElements';
import { cssVariables } from '../../styles/cssVariables';
import { pxToNumber } from '../../utils/cssUtils';
import { AdvancedFilters } from './AdvancedFiltersPopup';
import { MonitorContext } from './monitorContexts';
import {
  DateRangeMonitorFilter,
  MonitorFilterTag,
  MonitorFilterType,
  MonitorFiltersRegistration,
  StageMonitorFilter,
} from './MonitorFilter';
import { areMonitorFiltersEqual } from './monitorFilterUtils';
import { useMonitorViews } from './useMonitorViews';
import { useMonitorSearchFiltersStyles } from './MonitorSearchFilters.styles';

function FilterCol({ className, ...rest }) {
  const { styles } = useMonitorSearchFiltersStyles();
  return <Col className={classNames(styles.column, className)} {...rest} />;
}

const emptyValues = Object.fromEntries(
  Object.keys(MonitorFilterType).map(key => [
    MonitorFilterType[key].name,
    undefined,
  ])
);

function FiltersHeaderExtra({ view, setView }) {
  const views = useMonitorViews();

  return (
    <Row align="middle" gutter={pxToNumber(cssVariables.spaceNorm1_5)}>
      {views.map(({ name, disabled, disabledTooltipId, ...iconProps }) => {
        const active = name === view;

        return (
          <Col key={name}>
            <Tooltip
              title={
                disabled ? (
                  <FormattedMessage id={disabledTooltipId} />
                ) : undefined
              }
              placement="topLeft"
            >
              <IconButton
                active={active}
                disabled={disabled}
                size="sm"
                onClick={() => setView(name)}
                outline={false}
                {...iconProps}
              />
            </Tooltip>
          </Col>
        );
      })}
    </Row>
  );
}

function FiltersFooter() {
  const { data, activeSection, baseCounts } = useContext(MonitorContext);
  const intl = useIntl();
  const { styles } = useMonitorSearchFiltersStyles();

  const filteredCount = data[activeSection]?.totalRows || 0;
  const totalCount = baseCounts[activeSection]?.totalRows ?? 0;
  const stageLabel = intl.formatMessage({
    id: `monitor.stage.${activeSection}`,
  });

  return (
    <Row justify="end" align="middle">
      <Col>
        <span className={styles.paginationText}>
          <FormattedMessage
            id="monitor.filters.pagination"
            values={{
              filteredCount,
              totalCount,
              stageLabel: stageLabel?.toUpperCase(),
              bold: chunks => <strong>{chunks}</strong>,
            }}
          />
        </span>
      </Col>
    </Row>
  );
}

function AdvancedFilterTags() {
  const { styles } = useMonitorSearchFiltersStyles();
  return (
    <FilterCol flex="auto">
      <div className={styles.tags}>
        {Object.values(MonitorFilterType)
          .filter(def => def.isAdvanced)
          .map(def => (
            <MonitorFilterTag key={def.name} definition={def} />
          ))}
      </div>
    </FilterCol>
  );
}

function MainFilters() {
  return (
    <>
      <FilterCol>
        <StageMonitorFilter />
      </FilterCol>
      <FilterCol>
        <DateRangeMonitorFilter />
      </FilterCol>
      <FilterCol>
        <AdvancedFilters />
      </FilterCol>
    </>
  );
}

function ClearAllFiltersButton() {
  const { styles } = useMonitorSearchFiltersStyles();
  const { formInstance, forceUpdate } = useFormContext();
  const {
    filters: { setValues: setFilters, initialValues },
  } = useContext(FiltersAndSortContext);
  const { setActiveSection } = useContext(MonitorContext);

  const handleClearAll = () => {
    formInstance.resetFields();
    forceUpdate();
    setFilters(initialValues);
    setActiveSection(initialValues.stage);
  };

  return (
    <Col className={styles.buttons}>
      <InlineLink onClick={handleClearAll} textId="filters.buttons.clearAll" />
    </Col>
  );
}

function ContentLarge({ view, setView }) {
  const { isDialogOpen } = useContext(DialogsParentContext);
  const { styles: hasDialogStyles } = useHasDialogStyles();
  const { styles } = useMonitorSearchFiltersStyles();
  return (
    <div
      className={classNames('MonitorSearchFilters', styles.root, {
        [hasDialogStyles.hasDialog]: isDialogOpen,
      })}
    >
      <ConfigurableCard
        titleId="labels.filters"
        headerExtra={<FiltersHeaderExtra view={view} setView={setView} />}
      >
        <MainFilters />
        <AdvancedFilterTags />
        <ClearAllFiltersButton />
      </ConfigurableCard>
      <FiltersFooter />
    </div>
  );
}

function ContentSmall() {
  const { styles } = useMonitorSearchFiltersStyles();
  return (
    <Row
      className={classNames('MonitorSearchFilters', styles.root)}
      align="middle"
      justify="space-between"
    >
      <MainFilters />
    </Row>
  );
}

function SyncFormWithGlobalFilters({ values }) {
  const { formInstance, forceUpdate } = useFormContext();
  useEffect(() => {
    if (!areMonitorFiltersEqual(formInstance.getFieldsValue(), values)) {
      formInstance.setFieldsValue({ ...emptyValues, ...values });
      forceUpdate();
    }
  }, [values, formInstance, forceUpdate]);
  return null;
}

export default function MonitorFilters({ view, setView }) {
  const [form] = Form.useForm();
  const {
    filters: { initialValues, setValues: setFilters, values },
  } = useContext(FiltersAndSortContext);
  const { setActiveSection } = useContext(MonitorContext);

  const handleValuesChange = useCallback(
    (changedValues, allValues) => {
      if (changedValues.stage) {
        setActiveSection(changedValues.stage);
      }
      setFilters(allValues);
    },
    [setActiveSection, setFilters]
  );

  return (
    <DialogsParentProvider>
      <NoLabelForm
        form={form}
        className="no-margin-form-items"
        initialValues={initialValues}
        onValuesChange={handleValuesChange}
      >
        <SyncFormWithGlobalFilters values={values} />
        <MonitorFiltersRegistration />
        <div className="hide-md-and-smaller">
          <ContentLarge view={view} setView={setView} />
        </div>
        <div className="hide-lg-and-bigger">
          <ContentSmall />
        </div>
      </NoLabelForm>
    </DialogsParentProvider>
  );
}
